import { ReactNode } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import {
  VisitBucketKey,
  VisitFrequencyCounts,
  VisitFrequencyMode,
  VisitThresholds,
} from "../../types/visit-frequency.type";
import {
  BUCKETS_BY_MODE,
  BUCKET_LABEL,
  formatCount,
  formatPercent,
  thresholdText,
} from "./visitFrequency";

export interface CountsTableColumn {
  header: string;
  align?: "left" | "right" | "center";
  minWidth?: number;
}

export interface CountsTableRow {
  key: string;
  cells: ReactNode[];
  counts: VisitFrequencyCounts;
  /** Opens the next level for the whole row. */
  onOpen?: () => void;
  /** Opens the shop list for one bucket of this row. */
  onBucketClick?: (bucket: VisitBucketKey) => void;
  /** Rendered muted, e.g. a route with no active shops. */
  muted?: boolean;
}

interface CountsTableProps {
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
  leadColumns: CountsTableColumn[];
  rows: CountsTableRow[];
  total?: { label: string; counts: VisitFrequencyCounts };
  mode: VisitFrequencyMode;
  thresholds: VisitThresholds;
  workingDays: number;
  emptyText?: string;
}

const HEAD_BG = "#F8F9FC";
const TOTAL_BG = "#F2F4F8";
const NONE_BG = "#FFF8E1";

/**
 * The Excel รวม-sheet layout, shared by the branch and route levels: identity columns, then
 * shops, every bucket as จำนวน + %, then เข้าแต่ขายไม่ได้ and พักใช้งาน, and a รวม row.
 * A bucket count is a link into the shop list behind it.
 */
export default function CountsTable({
  title,
  subtitle,
  action,
  leadColumns,
  rows,
  total,
  mode,
  thresholds,
  workingDays,
  emptyText = "ไม่มีข้อมูลในช่วงที่เลือก",
}: Readonly<CountsTableProps>) {
  const buckets = BUCKETS_BY_MODE[mode];
  const columnCount = leadColumns.length + 1 + buckets.length * 2 + 2;

  return (
    <Paper sx={{ p: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        mb={1}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ "& td, & th": { whiteSpace: "nowrap" } }}>
          <TableHead sx={{ "& th": { bgcolor: HEAD_BG, fontWeight: 600 } }}>
            <TableRow>
              {leadColumns.map((column) => (
                <TableCell
                  key={column.header}
                  rowSpan={2}
                  align={column.align}
                  sx={{ minWidth: column.minWidth }}
                >
                  {column.header}
                </TableCell>
              ))}
              <TableCell rowSpan={2} align="right">
                จำนวนร้านค้า
              </TableCell>
              {buckets.map((bucket) => (
                <TableCell
                  key={bucket}
                  colSpan={2}
                  align="center"
                  sx={bucket === "none" ? { bgcolor: `${NONE_BG} !important` } : undefined}
                >
                  {BUCKET_LABEL[bucket]}
                  <Typography
                    component="div"
                    variant="caption"
                    color="text.secondary"
                    fontWeight={400}
                  >
                    {thresholdText(bucket, mode, thresholds, workingDays)}
                  </Typography>
                </TableCell>
              ))}
              <TableCell rowSpan={2} align="right">
                เข้าแต่ขายไม่ได้
              </TableCell>
              <TableCell rowSpan={2} align="right">
                พักใช้งาน
              </TableCell>
            </TableRow>
            <TableRow>
              {buckets.flatMap((bucket) => [
                <TableCell
                  key={`${bucket}-n`}
                  align="right"
                  sx={bucket === "none" ? { bgcolor: `${NONE_BG} !important` } : undefined}
                >
                  ร้าน
                </TableCell>,
                <TableCell
                  key={`${bucket}-p`}
                  align="right"
                  sx={bucket === "none" ? { bgcolor: `${NONE_BG} !important` } : undefined}
                >
                  %
                </TableCell>,
              ])}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    {emptyText}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow
                key={row.key}
                hover={Boolean(row.onOpen)}
                onClick={row.onOpen}
                sx={{
                  cursor: row.onOpen ? "pointer" : "default",
                  "& td": row.muted ? { color: "text.secondary" } : undefined,
                }}
              >
                {row.cells.map((cell, index) => (
                  <TableCell key={leadColumns[index]?.header ?? index} align={leadColumns[index]?.align}>
                    {cell}
                  </TableCell>
                ))}
                <CountCells
                  counts={row.counts}
                  buckets={buckets}
                  onBucketClick={row.onBucketClick}
                />
              </TableRow>
            ))}
            {total && rows.length > 0 && (
              <TableRow sx={{ "& td": { bgcolor: TOTAL_BG, fontWeight: 700 } }}>
                <TableCell colSpan={leadColumns.length}>{total.label}</TableCell>
                <CountCells counts={total.counts} buckets={buckets} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function CountCells({
  counts,
  buckets,
  onBucketClick,
}: Readonly<{
  counts: VisitFrequencyCounts;
  buckets: VisitBucketKey[];
  onBucketClick?: (bucket: VisitBucketKey) => void;
}>) {
  return (
    <>
      <TableCell align="right">{formatCount(counts.shops)}</TableCell>
      {buckets.flatMap((bucket) => {
        const count = counts.buckets[bucket] ?? 0;
        const shaded = bucket === "none" ? { bgcolor: NONE_BG } : undefined;
        return [
          <TableCell key={`${bucket}-n`} align="right" sx={shaded}>
            {onBucketClick && count > 0 ? (
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={(event) => {
                  // The row itself opens the next level; the count opens its shop list.
                  event.stopPropagation();
                  onBucketClick(bucket);
                }}
                sx={{ font: "inherit", fontWeight: "inherit" }}
              >
                {formatCount(count)}
              </Link>
            ) : (
              formatCount(count)
            )}
          </TableCell>,
          <TableCell key={`${bucket}-p`} align="right" sx={{ ...shaded, color: "text.secondary" }}>
            {formatPercent(count, counts.shops)}
          </TableCell>,
        ];
      })}
      <TableCell align="right">{formatCount(counts.visited_not_sold_shops)}</TableCell>
      <TableCell align="right">{formatCount(counts.suspended_shops)}</TableCell>
    </>
  );
}
